import React, { useState } from "react"
import { Modal, Button } from "antd"
import dayjs from "dayjs"
import {
  UsergroupAddOutlined,
  MailOutlined,
  EyeOutlined,
  CalendarOutlined,
  SendOutlined,
  CloseOutlined,
  SoundOutlined,
} from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import { getUserTimezone } from "../../utils/timezoneUtils"
import DetailsStep, { isValidEmailSender } from "./Steps/DetailsStep"
import AudienceStep from "./Steps/AudienceStep"
import ContentStep from "./Steps/ContentStep"
import PreviewStep from "./Steps/PreviewStep"
import ScheduleStep from "./Steps/ScheduleStep"
import {
  buildCampaignEmailBlock,
  parseStoredEmailDescription,
  enrichHtmlForEditor,
  CAMPAIGN_TEMPLATE_DEFAULT,
} from "../../utils/campaignEmailContent"
import "./Campaigns.css"

const TYPE_AUDIENCE_PRESETS = new Set([
  "All Users",
  "Paid Users",
  "Unpaid Users",
  "Active Users",
  "Inactive Users",
  "Returning Users",
  "Newsletter Users",
  "Contact Users",
])

const normalizeRecipientIds = (ids) =>
  (ids || []).map((id) => Number(id)).filter((id) => !isNaN(id))

const parseLegacyRecipients = (recipients) => {
  if (!recipients) return []
  if (Array.isArray(recipients)) {
    return recipients.map((id) => {
      const numId = Number(id)
      return isNaN(numId) ? id : numId
    })
  }
  if (typeof recipients === "string") {
    return recipients.split(",").map((id) => {
      const trimmed = id.trim()
      const numId = Number(trimmed)
      return isNaN(numId) ? trimmed : numId
    })
  }
  return []
}

const hasAudienceSelection = (formData) => {
  if (formData.file_path) return true
  if (formData.audiencePresets?.length > 0) return true
  if (formData.selectedUsers?.length > 0) return true
  if (formData.selectedContactUsers?.length > 0) return true
  if (formData.selectedContactGroups?.length > 0) return true
  return false
}

const buildAudiencePayloadFields = (formData) => {
  const audience_presets = [...(formData.audiencePresets || [])]
  const dashboard_recipients = normalizeRecipientIds(formData.selectedUsers)
  const contact_recipients = normalizeRecipientIds(formData.selectedContactUsers)
  const group_recipients = normalizeRecipientIds(formData.selectedContactGroups)
  const hasExcel = Boolean(formData.file_path)

  const fields = {
    audience_presets,
    dashboard_recipients,
    contact_recipients,
    group_recipients,
  }

  // Excel can be combined with selected users / presets / contacts
  if (hasExcel) {
    fields.file_path = formData.file_path
  }

  const sourceCount =
    (hasExcel ? 1 : 0) +
    (audience_presets.length > 0 ? 1 : 0) +
    (dashboard_recipients.length > 0 ? 1 : 0) +
    (contact_recipients.length > 0 ? 1 : 0) +
    (group_recipients.length > 0 ? 1 : 0)

  const isMixed = sourceCount > 1 || audience_presets.length > 1

  if (isMixed) {
    // Mixed audience (e.g. Excel + dashboard users) — keep all recipient arrays + file_path
    fields.audience_preset = "Custom Contact Users"
  } else if (hasExcel) {
    fields.audience_preset = "Excel Sheet"
  } else if (audience_presets.length === 1) {
    fields.audience_preset = audience_presets[0]
  } else if (dashboard_recipients.length > 0) {
    fields.audience_preset = "Custom Users"
  } else if (contact_recipients.length > 0) {
    fields.audience_preset = "Custom Contact Users"
  } else if (group_recipients.length > 0) {
    fields.audience_preset = "Contact Groups"
  } else if (
    formData.audience &&
    formData.audience.trim() &&
    !["Custom Users", "Contact Groups"].includes(formData.audience.trim())
  ) {
    fields.audience_preset = formData.audience.trim()
  }

  return fields
}

// Helper function to convert 12-hour format to 24-hour format
const convertTo24Hour = (time12h) => {
  if (!time12h) return null
  try {
    // Parse time in format "HH:mm A" (e.g., "04:00 AM")
    const time = dayjs(time12h, "hh:mm A")
    if (time.isValid()) {
      return time.format("HH:mm")
    }
    // If already in 24-hour format, return as is
    return time12h
  } catch {
    return time12h
  }
}

const CreateCampaignModal = ({
  visible,
  onClose,
  onSubmit,
  initialData,
  viewMode = false,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage

  const [currentStep, setCurrentStep] = useState(0)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSaveForLaterModal, setShowSaveForLaterModal] = useState(false)
  const [pendingPayload, setPendingPayload] = useState(null)
  const [scheduleStepError, setScheduleStepError] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    emailSender: "",
    campaignType: "Updates",
    audience: "",
    audiencePresets: [],
    selectedUsers: [],
    selectedContactUsers: [],
    selectedContactGroups: [],
    subjectEnglish: "",
    greetingEnglish: "",
    descriptionEnglish: "",
    subjectArabic: "",
    greetingArabic: "",
    descriptionArabic: "",
    template: "default",
    image: null,
    headerImage: null,
    footerImage: null,
    link: "",
    buttonText: "",
    buttonTextArabic: "",
    buttonLink: "",
    scheduleType: null,
    scheduledDate: null,
    scheduledTime: null,
    scheduled_timezone: null,
    schedule: null,
    file_path: null, // Store the File object, not the path
  })

  // Populate formData when initialData is provided (for editing)
  React.useEffect(() => {
    if (initialData && visible) {
      const originalData = initialData.originalData || initialData
      const emailBlock = originalData.email_block || {}

      // console.log("=== Editing Campaign Data ===")
      // console.log("originalData:", originalData)
      // console.log("emailBlock:", emailBlock)
      // console.log("originalData.subject_en:", originalData.subject_en)
      // console.log("originalData.subject_ar:", originalData.subject_ar)
      // console.log("emailBlock.subject_en:", emailBlock.subject_en)
      // console.log("emailBlock.subject_ar:", emailBlock.subject_ar)

      // Parse scheduled date and time - check multiple sources
      let scheduledDate = null
      let scheduledTime = null
      let scheduleType = "scheduled"

      // First check if date/time are in the record itself (from transformed data)
      if (initialData.scheduledOnDate) {
        scheduledDate = initialData.scheduledOnDate
      }
      if (initialData.scheduledOnTime) {
        // scheduledOnTime might be in 24-hour format (HH:mm) or already formatted
        const timeStr = initialData.scheduledOnTime
        if (timeStr.includes(":")) {
          const [hours, minutes] = timeStr.split(":")
          const hour = parseInt(hours, 10)
          if (!isNaN(hour) && hour >= 0 && hour < 24) {
            // It's in 24-hour format, convert to 12-hour
            const ampm = hour >= 12 ? "PM" : "AM"
            const hour12 = hour % 12 || 12
            scheduledTime = `${String(hour12).padStart(
              2,
              "0"
            )}:${minutes} ${ampm}`
          } else {
            // Already in 12-hour format
            scheduledTime = timeStr
          }
        } else {
          scheduledTime = timeStr
        }
      }

      // Fallback to originalData fields
      if (!scheduledDate && originalData.scheduled_on_date) {
        scheduledDate = originalData.scheduled_on_date
      }
      if (!scheduledTime && originalData.scheduled_on_time) {
        // Check if it's a datetime string or just time
        if (
          originalData.scheduled_on_time.includes("T") ||
          originalData.scheduled_on_time.includes(" ")
        ) {
          const scheduledDateObj = dayjs(originalData.scheduled_on_time)
          if (scheduledDateObj.isValid()) {
            if (!scheduledDate) {
              scheduledDate = scheduledDateObj.format("YYYY-MM-DD")
            }
            scheduledTime = scheduledDateObj.format("hh:mm A")
          }
        } else {
          // It's just time, convert from 24-hour to 12-hour format
          const time24 = originalData.scheduled_on_time
          const [hours, minutes] = time24.split(":")
          const hour = parseInt(hours, 10)
          if (!isNaN(hour)) {
            const ampm = hour >= 12 ? "PM" : "AM"
            const hour12 = hour % 12 || 12
            scheduledTime = `${String(hour12).padStart(
              2,
              "0"
            )}:${minutes} ${ampm}`
          }
        }
      }

      if (!scheduledDate && originalData.scheduled_date) {
        scheduledDate = originalData.scheduled_date
      }
      if (!scheduledTime && originalData.scheduled_time) {
        // Convert 24-hour format to 12-hour format
        const time24 = originalData.scheduled_time
        const [hours, minutes] = time24.split(":")
        const hour = parseInt(hours, 10)
        if (!isNaN(hour)) {
          const ampm = hour >= 12 ? "PM" : "AM"
          const hour12 = hour % 12 || 12
          scheduledTime = `${String(hour12).padStart(
            2,
            "0"
          )}:${minutes} ${ampm}`
        }
      }

      if (
        originalData.status === "Send Immediately" ||
        originalData.send_immediately
      ) {
        scheduleType = "immediately"
      } else if (
        originalData.status === "Draft" ||
        originalData.status === "Saved"
      ) {
        scheduleType = "save"
      }

      // Load audience from new split fields (with legacy recipients fallback)
      const audiencePresetValue =
        originalData.audience_preset || initialData.audience || ""

      let audiencePresets = Array.isArray(originalData.audience_presets)
        ? [...originalData.audience_presets]
        : []
      let selectedUsers = normalizeRecipientIds(originalData.dashboard_recipients)
      let selectedContactUsers = normalizeRecipientIds(
        originalData.contact_recipients
      )
      let selectedContactGroups = normalizeRecipientIds(
        originalData.group_recipients
      )

      const hasNewAudienceFields =
        audiencePresets.length > 0 ||
        selectedUsers.length > 0 ||
        selectedContactUsers.length > 0 ||
        selectedContactGroups.length > 0

      if (!hasNewAudienceFields && originalData.recipients) {
        const isContactUsersPreset =
          audiencePresetValue === "Custom Contact Users" ||
          audiencePresetValue === "Contact Users"
        const isContactGroupsPreset = audiencePresetValue === "Contact Groups"
        const legacyRecipients = parseLegacyRecipients(originalData.recipients)

        if (isContactGroupsPreset) {
          selectedContactGroups = legacyRecipients
        } else if (isContactUsersPreset) {
          selectedContactUsers = legacyRecipients
        } else {
          selectedUsers = legacyRecipients
        }
      }

      if (
        audiencePresets.length === 0 &&
        TYPE_AUDIENCE_PRESETS.has(audiencePresetValue)
      ) {
        audiencePresets = [audiencePresetValue]
      }

      const hasExcelFile = Boolean(
        originalData.file_path || initialData.file_path,
      )
      const hasOtherAudience =
        selectedUsers.length > 0 ||
        selectedContactUsers.length > 0 ||
        selectedContactGroups.length > 0 ||
        audiencePresets.length > 0

      // Don't lock UI to Excel-only when users/presets are also present
      const audience =
        hasExcelFile && !hasOtherAudience
          ? "Excel Sheet"
          : selectedUsers.length > 0
            ? "Custom Users"
            : audiencePresetValue === "Excel Sheet"
              ? ""
              : audiencePresetValue

      setFormData({
        title: originalData.title || initialData.campaign || "",
        description: originalData.description || "",
        emailSender:
          originalData.sender_email ||
          originalData.email_sender ||
          originalData.emailSender ||
          initialData.sender_email ||
          initialData.emailSender ||
          "",
        campaignType: originalData.type || "",
        audience,
        audiencePresets,
        selectedUsers,
        selectedContactUsers,
        selectedContactGroups,
        // Check multiple sources for subject fields - try all possible locations
        subjectEnglish:
          originalData.subject_en ||
          originalData.subject_english ||
          emailBlock.subject_en ||
          emailBlock.subject_english ||
          initialData.subjectEnglish ||
          "",
        subjectArabic:
          originalData.subject_ar ||
          originalData.subject_arabic ||
          emailBlock.subject_ar ||
          emailBlock.subject_arabic ||
          initialData.subjectArabic ||
          "",
        greetingEnglish: emailBlock.greeting_en || "",
        descriptionEnglish: emailBlock.text_en
          ? enrichHtmlForEditor(parseStoredEmailDescription(emailBlock.text_en))
          : "",
        greetingArabic: emailBlock.greeting_ar || "",
        descriptionArabic: emailBlock.text_ar
          ? enrichHtmlForEditor(parseStoredEmailDescription(emailBlock.text_ar))
          : "",
        template:
          emailBlock.template ||
          originalData.template ||
          CAMPAIGN_TEMPLATE_DEFAULT,
        image: emailBlock.image_url || initialData.image || null,
        headerImage: emailBlock.header_image_url || null,
        footerImage: emailBlock.footer_image_url || null,
        link: emailBlock.button_link || "",
        buttonText: emailBlock.button_label_en || "",
        buttonTextArabic: emailBlock.button_label_ar || "",
        buttonLink: emailBlock.button_link || "",
        scheduleType: scheduleType,
        scheduledDate: scheduledDate,
        scheduledTime: scheduledTime,
        scheduled_timezone: originalData.scheduled_timezone || null,
        schedule: null,
        file_path: originalData.file_path || null,
      })
      setCurrentStep(0) // Reset to first step when editing
    } else if (!initialData && visible) {
      // Reset form when creating new campaign
      setFormData({
        title: "",
        description: "",
        emailSender: "",
        campaignType: "Updates",
        audience: "",
        audiencePresets: [],
        selectedUsers: [],
        selectedContactUsers: [],
        selectedContactGroups: [],
        subjectEnglish: "",
        greetingEnglish: "",
        descriptionEnglish: "",
        subjectArabic: "",
        greetingArabic: "",
        descriptionArabic: "",
        template: "default",
        image: null,
        headerImage: null,
        footerImage: null,
        link: "",
        buttonText: "",
        buttonTextArabic: "",
        buttonLink: "",
        scheduleType: null,
        scheduledDate: null,
        scheduledTime: null,
        scheduled_timezone: null,
        schedule: null,
        file_path: null,
      })
      setCurrentStep(0)
    }
  }, [initialData, visible])

  // Clear schedule error when user selects an option
  React.useEffect(() => {
    if (formData.scheduleType) {
      setScheduleStepError(false)
    }
  }, [formData.scheduleType])

  const steps = [
    {
      title: t?.steps?.details || "Details",
      icon: null,
      content: (
        <DetailsStep
          formData={formData}
          setFormData={setFormData}
          viewMode={viewMode}
        />
      ),
    },
    {
      title: t?.steps?.audience || "Audience",
      icon: <UsergroupAddOutlined className="create-campaign-step-icon" />,
      content: (
        <AudienceStep
          key={`audience-${initialData?.originalData?.id || initialData?.id || "new"}-${viewMode ? "view" : "edit"}`}
          formData={formData}
          setFormData={setFormData}
          viewMode={viewMode}
          campaignId={initialData?.originalData?.id || initialData?.id}
          autoSelectContactList={
            !!(
              initialData?.originalData?.id &&
              ((initialData?.originalData?.contact_recipients?.length > 0) ||
                (initialData?.originalData?.group_recipients?.length > 0) ||
                (initialData?.originalData?.dashboard_recipients?.length > 0) ||
                (initialData?.originalData?.audience_presets?.length > 0) ||
                (["Custom Contact Users", "Contact Groups", "Custom Users"].includes(
                  initialData?.originalData?.audience_preset
                ) &&
                  initialData?.originalData?.recipients &&
                  (Array.isArray(initialData.originalData.recipients)
                    ? initialData.originalData.recipients.length > 0
                    : initialData.originalData.recipients.length > 0)))
            )
          }
          audiencePreset={initialData?.originalData?.audience_preset}
        />
      ),
    },
    {
      title: t?.steps?.content || "Content",
      icon: <MailOutlined className="create-campaign-step-icon" />,
      content: (
        <ContentStep
          formData={formData}
          setFormData={setFormData}
          viewMode={viewMode}
        />
      ),
    },
    {
      title: t?.steps?.preview || "Preview",
      icon: <EyeOutlined className="create-campaign-step-icon" />,
      content: (
        <PreviewStep
          formData={formData}
          setFormData={setFormData}
          viewMode={viewMode}
        />
      ),
    },
    {
      title: t?.steps?.schedule || "Schedule",
      icon: <CalendarOutlined className="create-campaign-step-icon" />,
      content: (
        <ScheduleStep
          formData={formData}
          setFormData={setFormData}
          viewMode={viewMode}
          showScheduleError={scheduleStepError}
        />
      ),
    },
  ]

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Validate required fields for current step
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Details step
        if (!formData.title || !formData.title.trim()) return false
        if (!formData.campaignType || !formData.campaignType.trim())
          return false
        if (!isValidEmailSender(formData.emailSender)) return false
        return true

      case 1: // Audience step
        return hasAudienceSelection(formData)

      case 2: // Content step
        if (!formData.subjectArabic || !formData.subjectArabic.trim())
          return false
        return true

      case 3: // Preview step - no validation needed
        return true

      case 4: // Schedule step
        // If scheduleType is "scheduled", date and time are required
        if (formData.scheduleType === "scheduled") {
          if (!formData.scheduledDate) return false
          if (!formData.scheduledTime) return false
        }
        return true

      default:
        return true
    }
  }

  // Validate required fields for "Schedule for Later" (final validation)
  const validateRequiredFields = () => {
    // Basic required fields
    if (!formData.title || !formData.title.trim()) return false
    if (!formData.campaignType || !formData.campaignType.trim()) return false
    if (!isValidEmailSender(formData.emailSender)) return false

    // Audience is required - at least one audience source or Excel file
    if (!hasAudienceSelection(formData)) return false

    if (!formData.subjectArabic || !formData.subjectArabic.trim()) return false

    // If scheduleType is "scheduled", date and time are required
    if (formData.scheduleType === "scheduled") {
      if (!formData.scheduledDate) return false
      if (!formData.scheduledTime) return false
    }

    return true
  }

  const isCurrentStepValid = validateCurrentStep()
  const isFormValid = validateRequiredFields()

  const handleSubmit = () => {
    // Require a schedule option to be selected
    if (!formData.scheduleType) {
      setScheduleStepError(true)
      return
    }
    setScheduleStepError(false)

    const emailBlock = buildCampaignEmailBlock(formData)

    let status = "Draft"
    if (formData.scheduleType === "immediately") {
      status = "Send Immediately"
    } else if (formData.scheduleType === "scheduled") {
      status = "Scheduled"
    } else if (formData.scheduleType === "save") {
      status = "Saved"
    }

    // Build payload - only include non-empty fields
    const payload = {}

    // Add is_draft based on whether we're editing or creating
    if (initialData) {
      payload.is_draft = false
    }

    if (formData.title && formData.title.trim()) {
      payload.title = formData.title.trim()
    }
    if (formData.description && formData.description.trim()) {
      payload.description = formData.description.trim()
    }
    if (formData.emailSender && formData.emailSender.trim()) {
      payload.sender_email = formData.emailSender.trim()
    }
    if (formData.campaignType && formData.campaignType.trim()) {
      payload.type = formData.campaignType.trim()
    }
    if (status) {
      payload.status = status
    }
    if (formData.subjectEnglish && formData.subjectEnglish.trim()) {
      payload.subject_en = formData.subjectEnglish.trim()
    }
    if (formData.subjectArabic && formData.subjectArabic.trim()) {
      payload.subject_ar = formData.subjectArabic.trim()
    }
    if (Object.keys(emailBlock).length > 0) {
      payload.email_block = emailBlock
    }
    payload.template = formData.template || CAMPAIGN_TEMPLATE_DEFAULT

    Object.assign(payload, buildAudiencePayloadFields(formData))

    // Add scheduled_date and scheduled_time when schedule type is "scheduled"
    if (formData.scheduleType === "scheduled") {
      if (formData.scheduledDate) {
        payload.scheduled_date = formData.scheduledDate
      }
      // Always include scheduled_time if scheduled_date exists, use default if not set
      if (formData.scheduledTime) {
        // Convert time from "HH:mm A" format to "HH:mm" (24-hour format) for API
        const time24 = convertTo24Hour(formData.scheduledTime)
        if (time24) {
          payload.scheduled_time = time24
        } else {
          // Fallback to default time if conversion fails
          payload.scheduled_time = "00:00"
        }
      } else if (formData.scheduledDate) {
        // If date is set but time is not, use default time
        payload.scheduled_time = "00:00"
      }
    }

    // Always include timezone - use formData timezone if available, otherwise get current timezone
    payload.scheduled_timezone =
      formData.scheduled_timezone || getUserTimezone()

    // Regular JSON payload (file_path is now a string, not a File object)
    const cleanPayload = JSON.parse(JSON.stringify(payload))

    // Show confirmation modal for "Send Immediately", "Schedule for Later", or "Save for Later"
    if (
      formData.scheduleType === "immediately" ||
      formData.scheduleType === "scheduled" ||
      formData.scheduleType === "save"
    ) {
      setPendingPayload(cleanPayload)
      setShowConfirmModal(true)
    }
  }

  const handleConfirmSubmit = () => {
    if (onSubmit && pendingPayload) {
      onSubmit(pendingPayload)
    }
    setShowConfirmModal(false)
    setPendingPayload(null)
    handleClose(true) // Pass true to skip draft save
  }

  const handleCancelConfirm = () => {
    setShowConfirmModal(false)
    setPendingPayload(null)
    // The main modal will automatically show again since showConfirmModal is false
  }

  const handleSaveForLater = () => {
    if (onSubmit && pendingPayload) {
      onSubmit(pendingPayload)
    }
    setShowSaveForLaterModal(false)
    setPendingPayload(null)
    handleClose(true) // Pass true to skip draft save
  }

  const handleDeleteCampaign = () => {
    setShowSaveForLaterModal(false)
    setPendingPayload(null)
    // Reset form data
    setCurrentStep(0)
    setFormData({
      title: "",
      description: "",
      emailSender: "",
      campaignType: "Updates",
      audience: "",
      audiencePresets: [],
      selectedUsers: [],
      selectedContactUsers: [],
      selectedContactGroups: [],
      subjectEnglish: "",
      greetingEnglish: "",
      descriptionEnglish: "",
      subjectArabic: "",
      greetingArabic: "",
      descriptionArabic: "",
      template: "default",
      image: null,
      headerImage: null,
      footerImage: null,
      link: "",
      buttonText: "",
      buttonTextArabic: "",
      buttonLink: "",
      scheduleType: null,
      scheduledDate: null,
      scheduledTime: null,
      schedule: null,
      file_path: null,
    })
    // Close the modal and return to campaigns main page
    onClose()
  }

  const handleCancelSaveForLater = () => {
    setShowSaveForLaterModal(false)
    setPendingPayload(null)
    // The main modal will automatically show again since showSaveForLaterModal is false
  }

  const buildDraftPayload = () => {
    const emailBlock = buildCampaignEmailBlock(formData)

    // Build payload - include all filled data
    const payload = {
      is_draft: true,
    }

    // Only add status if scheduleType is selected
    if (formData.scheduleType) {
      if (formData.scheduleType === "immediately") {
        payload.status = "Send Immediately"
      } else if (formData.scheduleType === "scheduled") {
        payload.status = "Scheduled"
      } else if (formData.scheduleType === "save") {
        payload.status = "Saved"
      }
    }

    if (formData.title && formData.title.trim()) {
      payload.title = formData.title.trim()
    }
    if (formData.description && formData.description.trim()) {
      payload.description = formData.description.trim()
    }
    if (formData.emailSender && formData.emailSender.trim()) {
      payload.sender_email = formData.emailSender.trim()
    }
    if (formData.campaignType && formData.campaignType.trim()) {
      payload.type = formData.campaignType.trim()
    }
    if (formData.subjectEnglish && formData.subjectEnglish.trim()) {
      payload.subject_en = formData.subjectEnglish.trim()
    }
    if (formData.subjectArabic && formData.subjectArabic.trim()) {
      payload.subject_ar = formData.subjectArabic.trim()
    }
    if (Object.keys(emailBlock).length > 0) {
      payload.email_block = emailBlock
    }
    payload.template = formData.template || CAMPAIGN_TEMPLATE_DEFAULT

    Object.assign(payload, buildAudiencePayloadFields(formData))

    // Add scheduled_date and scheduled_time if they exist
    if (formData.scheduledDate) {
      payload.scheduled_date = formData.scheduledDate
    }
    if (formData.scheduledTime) {
      // Convert time from "HH:mm A" format to "HH:mm" (24-hour format) for API
      const time24 = convertTo24Hour(formData.scheduledTime)
      if (time24) {
        payload.scheduled_time = time24
      }
    }
    // Always include timezone - use formData timezone if available, otherwise get current timezone
    payload.scheduled_timezone =
      formData.scheduled_timezone || getUserTimezone()

    // Regular JSON payload (file_path is now a string, not a File object)
    return JSON.parse(JSON.stringify(payload))
  }

  const handleClose = async (skipDraftSave = false) => {
    // If we're already submitting (from confirm modals), just close without showing modal
    if (skipDraftSave) {
      setCurrentStep(0)
      setFormData({
        title: "",
        description: "",
        emailSender: "",
        campaignType: "Updates",
        audience: "",
        audiencePresets: [],
        selectedUsers: [],
        selectedContactUsers: [],
        selectedContactGroups: [],
        subjectEnglish: "",
        greetingEnglish: "",
        descriptionEnglish: "",
        subjectArabic: "",
        greetingArabic: "",
        descriptionArabic: "",
        template: "default",
        image: null,
        headerImage: null,
        footerImage: null,
        link: "",
        buttonText: "",
        buttonTextArabic: "",
        buttonLink: "",
        scheduleType: null,
        scheduledDate: null,
        scheduledTime: null,
        scheduled_timezone: null,
        schedule: null,
        file_path: null,
      })
      onClose()
      return
    }

    // Check if there's any data filled
    const hasData =
      formData.title ||
      formData.description ||
      formData.emailSender ||
      formData.campaignType ||
      formData.audience ||
      (formData.audiencePresets && formData.audiencePresets.length > 0) ||
      (formData.selectedUsers && formData.selectedUsers.length > 0) ||
      (formData.selectedContactUsers &&
        formData.selectedContactUsers.length > 0) ||
      (formData.selectedContactGroups &&
        formData.selectedContactGroups.length > 0) ||
      formData.subjectEnglish ||
      formData.subjectArabic ||
      formData.descriptionEnglish ||
      formData.descriptionArabic ||
      formData.image ||
      formData.headerImage ||
      formData.footerImage ||
      formData.buttonText ||
      formData.buttonTextArabic ||
      formData.buttonLink ||
      formData.scheduledDate ||
      formData.scheduledTime

    // IMPORTANT: This modal ONLY shows when clicking cross (X) button
    // It does NOT show when:
    // - Editing a campaign (!initialData check prevents this)
    // - In view mode (viewMode check prevents this)
    // - Clicking "Save for Later" button (handleClose(true) skips this)
    if (hasData && !initialData && !viewMode) {
      const draftPayload = buildDraftPayload()
      setPendingPayload(draftPayload)
      setShowSaveForLaterModal(true)
      return // Don't close the modal yet, let the save-for-later modal handle it
    } else {
      // No data, just close
      setCurrentStep(0)
      setFormData({
        title: "",
        description: "",
        emailSender: "",
        campaignType: "Updates",
        audience: "",
        audiencePresets: [],
        selectedUsers: [],
        selectedContactUsers: [],
        selectedContactGroups: [],
        subjectEnglish: "",
        greetingEnglish: "",
        descriptionEnglish: "",
        subjectArabic: "",
        greetingArabic: "",
        descriptionArabic: "",
        template: "default",
        image: null,
        headerImage: null,
        footerImage: null,
        link: "",
        buttonText: "",
        buttonTextArabic: "",
        buttonLink: "",
        scheduleType: null,
        scheduledDate: null,
        scheduledTime: null,
        schedule: null,
        file_path: null,
      })
      onClose()
    }
  }

  return (
    <>
      <Modal
        title={
          <div className="create-campaign-modal-title-text">
            {viewMode
              ? t?.view_campaign_details || "View Campaign Details"
              : initialData
              ? t?.edit_campaign || "Edit Campaign"
              : t?.create_campaign || "Create Campaign"}
          </div>
        }
        open={visible && !showConfirmModal && !showSaveForLaterModal}
        onCancel={handleClose}
        footer={null}
        width={900}
        className="create-campaign-modal"
        closeIcon={
          <span
            className="create-campaign-modal-close-icon"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleClose()
            }}
            style={{ cursor: "pointer" }}
          >
            ×
          </span>
        }
      >
        <div className="create-campaign-modal-content-wrapper">
          {/* Custom Progress Tracker */}
          <div className="custom-progress-tracker">
            {steps.map((step, index) => {
              // In view mode, show all steps as completed
              const isCompleted = viewMode ? true : index < currentStep
              const isCurrent = viewMode ? false : index === currentStep
              const isFuture = viewMode ? false : index > currentStep

              // Get icon based on step state
              // In view mode, all steps show as completed (white icons)
              let stepIcon = step.icon
              if (index === 0) {
                // Details step - show white icon when completed, black when current
                stepIcon = (
                  <img
                    src={
                      isCompleted || viewMode
                        ? "/Campaign/create-campaign-white.svg"
                        : "/Campaign/create-campaign-black.svg"
                    }
                    alt="Details"
                    className="create-campaign-step-image"
                  />
                )
              } else if (index === 1) {
                stepIcon = (
                  <img
                    src={
                      isCompleted || viewMode
                        ? "/Campaign/user-group-white.svg"
                        : "/Campaign/user-group-cam.svg"
                    }
                    alt="Details"
                    className="create-campaign-step-image"
                  />
                )
              } else if (index === 2) {
                stepIcon = (
                  <img
                    src={
                      isCompleted || viewMode
                        ? "/Campaign/mail-edit-white.svg"
                        : "/Campaign/mail-edit-cam.svg"
                    }
                    alt="Details"
                    className="create-campaign-step-image"
                  />
                )
              } else if (index === 3) {
                stepIcon = (
                  <img
                    src={
                      isCompleted || viewMode
                        ? "/Campaign/view-white.svg"
                        : "/Campaign/view-cam.svg"
                    }
                    alt="Details"
                    className="create-campaign-step-image"
                  />
                )
              } else {
                stepIcon = (
                  <img
                    src={
                      isCompleted || viewMode
                        ? "/Campaign/calendar-cam.svg"
                        : "/Campaign/calendar-cam.svg"
                    }
                    alt="Details"
                    className="create-campaign-step-image"
                  />
                )
              }

              return (
                <React.Fragment key={index}>
                  <div className="progress-step-wrapper">
                    <div
                      className={`progress-step-icon ${
                        isCompleted
                          ? "progress-step-completed"
                          : isCurrent
                          ? "progress-step-current"
                          : "progress-step-future"
                      }`}
                    >
                      <div className="progress-step-icon-content">
                        {stepIcon}
                      </div>
                    </div>
                    <div
                      className={`progress-step-title ${
                        isCompleted
                          ? "progress-step-title-completed"
                          : isCurrent
                          ? "progress-step-title-current"
                          : "progress-step-title-future"
                      }`}
                    >
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`progress-step-connector ${
                        isCompleted
                          ? "progress-step-connector-completed"
                          : "progress-step-connector-future"
                      }`}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>

          <div className="create-campaign-modal-step-content">
            {steps[currentStep].content}
          </div>

          {!viewMode && (
            <div className="create-campaign-modal-buttons-container">
              <Button
                onClick={currentStep === 0 ? handleClose : handlePrev}
                className="create-campaign-modal-button"
              >
                {currentStep === 0
                  ? t?.buttons?.cancel || "Cancel"
                  : t?.buttons?.back || "Back"}
              </Button>
              <Button
                type="primary"
                onClick={
                  currentStep === steps.length - 1 ? handleSubmit : handleNext
                }
                className="create-campaign-modal-button-primary"
                disabled={
                  currentStep === steps.length - 1
                    ? formData.scheduleType === "scheduled" && !isFormValid
                    : !isCurrentStepValid
                }
              >
                {currentStep === steps.length - 1
                  ? initialData
                    ? t?.buttons?.update_campaign || "Update Campaign"
                    : t?.buttons?.create_campaign || "Create Campaign"
                  : t?.buttons?.next || "Next"}
              </Button>
            </div>
          )}
          {viewMode && (
            <div className="create-campaign-modal-buttons-container">
              <Button
                onClick={currentStep === 0 ? handleClose : handlePrev}
                className="create-campaign-modal-button"
              >
                {currentStep === 0
                  ? t?.buttons?.close || "Close"
                  : t?.buttons?.back || "Back"}
              </Button>
              {currentStep < steps.length - 1 && (
                <Button
                  type="primary"
                  onClick={handleNext}
                  className="create-campaign-modal-button-primary"
                >
                  {t?.buttons?.next || "Next"}
                </Button>
              )}
              {currentStep === steps.length - 1 && (
                <Button
                  type="primary"
                  onClick={handleClose}
                  className="create-campaign-modal-button-primary"
                >
                  {t?.buttons?.close || "Close"}
                </Button>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onCancel={handleCancelConfirm}
        footer={null}
        width={500}
        className="confirm-campaign-modal"
        closeIcon={
          <CloseOutlined className="confirm-campaign-modal-close-icon" />
        }
      >
        <div className="confirm-campaign-content">
          <div className="confirm-campaign-icon">
            <div className="confirm-campaign-icon-container">
              <img
                src="/Campaign/elements-confirm.svg"
                alt=""
              />
            </div>
          </div>
          <h2 className="confirm-campaign-title">
            {t?.confirm?.title || "Confirm Campaign"}
          </h2>
          <p className="confirm-campaign-message">
            {formData.scheduleType === "immediately"
              ? t?.confirm?.send_immediately_message ||
                "Campaign will be sent immediately"
              : formData.scheduleType === "save"
              ? t?.confirm?.save_for_later_message ||
                "Campaign will be saved for later"
              : t?.confirm?.schedule_for_later_message ||
                "Campaign will be scheduled for later"}
          </p>
          <Button
            type="primary"
            onClick={handleConfirmSubmit}
            className="confirm-campaign-button"
          >
            {t?.buttons?.confirm_campaign || "Confirm Campaign"}
          </Button>
        </div>
      </Modal>

      {/* Save for Later Modal */}
      <Modal
        open={showSaveForLaterModal}
        onCancel={handleCancelSaveForLater}
        footer={null}
        width={500}
        className="save-for-later-modal"
        closeIcon={
          <CloseOutlined className="save-for-later-modal-close-icon" />
        }
      >
        <div className="save-for-later-content">
          <div className="save-for-later-icon">
            <div className="save-for-later-icon-bg">
              <div className="confirm-campaign-icon-container">
                <img
                  src="/Campaign/marketing-modal.svg"
                  alt=""
                />
              </div>
            </div>
          </div>
          <h2 className="save-for-later-title">
            {t?.save_for_later_modal?.title || "Save or delete your campaign?"}
          </h2>
          <p className="save-for-later-subtitle">
            {t?.save_for_later_modal?.subtitle ||
              "Manage your campaign progress"}
          </p>
          <div className="save-for-later-buttons">
            <Button
              onClick={handleDeleteCampaign}
              className="save-for-later-delete-btn"
            >
              {t?.buttons?.delete_campaign || "Delete Campaign"}
            </Button>
            <Button
              type="primary"
              onClick={handleSaveForLater}
              className="save-for-later-save-btn"
            >
              {t?.buttons?.save_for_later || "Save For Later"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default CreateCampaignModal
